import {
  COLOR_DARK_TEXT,
  COLOR_LIGHT_TEXT,
  getContrastTextColor,
  isLightColor,
  toVeryDarkVariant,
  toVeryLightVariant,
} from '@shared/utils/color.utils';
import { HdBodygraphCenter } from './hd-bodygraph.models';

export interface HdBodygraphColorConfig {
  /** Color applied to channel segments with personality (consciousness) activation. */
  channelPersonality: string;

  /** Color applied to channel segments with design (unconscious) activation. */
  channelDesign: string;

  /**
   * Color applied to channel/gate segments with a 'highlight' activation, used by
   * circuit highlighting and other educational overlays. Shares the same blue tone
   * as the planet-grid primary color.
   */
  channelHighlight: string;

  /** Color of an inactive (empty) channel segment. Uses SVG original fill when null. */
  channelInactive: string | null;

  /** Fill color of gate activation circles when a gate is active and the 'black' preset is used. */
  gateCircleFill: string;

  /** Text color of gate numbers inside activation circles when the 'black' preset is used. */
  gateCircleTextColor: string;

  /** Text color of gate numbers when the activation circle is not rendered. */
  gateNumberTextColor: string;

  /**
   * Drives how the colors of the activation circle (and the gate number inside it)
   * are resolved for each active gate. See {@link resolveHdBodygraphActiveGateCircleColors}.
   */
  activeGateColorPreset: HdBodygraphActiveGateColorPreset;

  /** Background color per center when it is defined (has at least one defined channel). */
  centerDefined: Readonly<Record<HdBodygraphCenter, string>>;

  /** Background color for an undefined center (has active gates but no defined channel). */
  centerUndefined: string;

  /** Background color for an open center (no active gates at all). Uses SVG original fill when null. */
  centerOpen: string | null;

  /** Visual style for the center borders. */
  centerBorderStyle: HdBodygraphCenterBorderStyle;

  /** Border color for undefined and open centers. */
  centerBorderColor: string;
}

export type HdBodygraphCenterBorderStyle = 'none' | 'thin' | 'medium' | 'thick';

/**
 * Selects how the activation circles around active gate numbers are colored:
 * - `'source'`: personality/design source colors, with a split circle for both.
 * - `'black'`: static palette using `gateCircleFill` / `gateCircleTextColor`.
 * - `'dynamic'`: derives the circle and text color per gate from the center it belongs to.
 */
export type HdBodygraphActiveGateColorPreset = 'source' | 'black' | 'dynamic';

export interface HdBodygraphActiveGateCircleColors {
  circleFill: string;
  textColor: string;
}

/** Fallback used by the dynamic preset when the gate sits in an undefined center. */
const HD_BODYGRAPH_DYNAMIC_UNDEFINED_CIRCLE_FILL = 'rgb(38,38,38)';

/**
 * HSL lightness used for the "donker" circle variant in light centers. Iets opgetild
 * t.o.v. de generieke `toVeryDarkVariant` default zodat de cirkels niet te dicht bij
 * zwart aanleunen en de center-tint nog herkenbaar blijft.
 */
const HD_BODYGRAPH_DYNAMIC_DARK_CIRCLE_LIGHTNESS = 0.2;

/**
 * Resolves the colors of the activation circle and the gate number inside it for a
 * given gate. The center context is needed for the `'dynamic'` preset so the circle
 * harmonizes with its center's tint.
 */
export function resolveHdBodygraphActiveGateCircleColors(
  colors: HdBodygraphColorConfig,
  center: HdBodygraphCenter,
  isCenterDefined: boolean,
): HdBodygraphActiveGateCircleColors {
  if (colors.activeGateColorPreset !== 'dynamic') {
    return { circleFill: colors.gateCircleFill, textColor: colors.gateCircleTextColor };
  }

  if (!isCenterDefined) {
    return { circleFill: HD_BODYGRAPH_DYNAMIC_UNDEFINED_CIRCLE_FILL, textColor: COLOR_LIGHT_TEXT };
  }

  const centerColor = colors.centerDefined[center];

  if (isLightColor(centerColor)) {
    return {
      circleFill: toVeryDarkVariant(centerColor, HD_BODYGRAPH_DYNAMIC_DARK_CIRCLE_LIGHTNESS),
      textColor: COLOR_LIGHT_TEXT,
    };
  }

  return { circleFill: toVeryLightVariant(centerColor), textColor: COLOR_DARK_TEXT };
}

/**
 * Determines the best contrasting text color (dark or light) for a given background color.
 * Uses the WCAG relative luminance formula to ensure readable contrast.
 */
export function getHdBodygraphContrastTextColor(backgroundColor: string | null): string {
  return isHdBodygraphDarkCenterColor(backgroundColor) ? HD_CENTER_LIGHT_TEXT : getContrastTextColor(backgroundColor);
}

/** Gebroken, bijna wit: tekst op een donkere centerkleur (Steven 2026-10-17). */
export const HD_CENTER_LIGHT_TEXT = 'rgb(247,244,238)';

/**
 * Luminantiegrens waaronder een centerkleur "donker" telt (navy/donkerrood van
 * Whale zitten rond 0,03-0,06). Bewust ver onder de generieke 0,4 van
 * {@link getContrastTextColor}: de donkerste center van de klassieke paletten
 * (Enhanced Deep root, ~0,19) moet donkere tekst houden zoals altijd.
 */
const HD_BODYGRAPH_DARK_CENTER_LUMINANCE = 0.12;

/** Of tekst en lichte vlakken op deze centerkleur een lichte variant nodig hebben. */
export function isHdBodygraphDarkCenterColor(color: string | null | undefined): boolean {
  return getContrastTextColor(color, HD_BODYGRAPH_DARK_CENTER_LUMINANCE) === COLOR_LIGHT_TEXT;
}

/** Tekstkleur voor poortnummers, badges en labels die rechtstreeks op een centervulling staan. */
export function resolveHdBodygraphCenterTextColor(centerFill: string | null | undefined): string {
  return isHdBodygraphDarkCenterColor(centerFill) ? HD_CENTER_LIGHT_TEXT : COLOR_DARK_TEXT;
}

export type HdBodygraphColorConfigPresetId =
  | 'classic'
  | 'enhanced-light'
  | 'enhanced-deep'
  | 'warm-dusk'
  | 'warm-dusk-subtle'
  | 'whales-of-personality';

export interface HdBodygraphColorConfigPreset {
  id: HdBodygraphColorConfigPresetId;
  label: string;
  config: Readonly<HdBodygraphColorConfig>;
}

const HD_BODYGRAPH_CLASSIC_CENTER_COLORS: Readonly<Record<HdBodygraphCenter, string>> = {
  hoofd: 'rgb(234,244,121)',
  ajna: 'rgb(73,234,113)',
  keel: 'rgb(201,169,125)',
  zelf: 'rgb(234,244,121)',
  hart: 'rgb(255,88,91)',
  milt: 'rgb(201,169,125)',
  sacraal: 'rgb(255,88,91)',
  solarplexus: 'rgb(201,169,125)',
  root: 'rgb(201,169,125)',
};

const HD_BODYGRAPH_BASE_COLOR_CONFIG: Readonly<Omit<HdBodygraphColorConfig, 'centerDefined'>> = {
  channelPersonality: 'rgb(14,14,14)',
  channelDesign: 'rgb(214,0,0)',
  channelHighlight: 'rgb(33,141,240)',
  channelInactive: null,
  gateCircleFill: 'rgb(14,14,14)',
  gateCircleTextColor: 'rgb(255,255,255)',
  gateNumberTextColor: 'rgb(14,14,14)',
  activeGateColorPreset: 'source',
  centerUndefined: 'rgb(244,244,244)',
  centerOpen: 'rgb(250,250,250)',
  centerBorderStyle: 'thin',
  centerBorderColor: 'rgb(130,130,130)',
};

export const HD_BODYGRAPH_CLASSIC_COLOR_CONFIG: Readonly<HdBodygraphColorConfig> = {
  ...HD_BODYGRAPH_BASE_COLOR_CONFIG,
  centerDefined: HD_BODYGRAPH_CLASSIC_CENTER_COLORS,
};

export const HD_BODYGRAPH_ENHANCED_LIGHT_COLOR_CONFIG: Readonly<HdBodygraphColorConfig> = {
  ...HD_BODYGRAPH_BASE_COLOR_CONFIG,
  centerDefined: {
    hoofd: 'rgb(234,244,121)',
    ajna: 'rgb(94,254,158)',
    keel: 'rgb(229,200,123)',
    zelf: 'rgb(254,248,94)',
    hart: 'rgb(255,158,170)',
    milt: 'rgb(255,229,163)',
    sacraal: 'rgb(255,120,112)',
    solarplexus: 'rgb(255,169,106)',
    root: 'rgb(212,168,105)',
  },
};

export const HD_BODYGRAPH_ENHANCED_DEEP_COLOR_CONFIG: Readonly<HdBodygraphColorConfig> = {
  ...HD_BODYGRAPH_BASE_COLOR_CONFIG,
  centerDefined: {
    hoofd: 'rgb(234,244,121)',
    ajna: 'rgb(73,234,113)',
    keel: 'rgb(201,169,125)',
    zelf: 'rgb(254,248,94)',
    hart: 'rgb(255,109,131)',
    milt: 'rgb(250,200,132)',
    sacraal: 'rgb(255,63,66)',
    solarplexus: 'rgb(255,144,45)',
    root: 'rgb(154,113,56)',
  },
};

/**
 * Warm Dusk variant of Enhanced Deep — minimal warm-dusk shift only on
 * center colors (every hue nudged slightly warmer/duskier without losing
 * its identity). Channels, gate circles and the rest of the base config
 * stay identical to Enhanced Deep so the chart still reads exactly the
 * same; only the saturation/temperature of the center fills changes.
 */
export const HD_BODYGRAPH_WARM_DUSK_COLOR_CONFIG: Readonly<HdBodygraphColorConfig> = {
  ...HD_BODYGRAPH_BASE_COLOR_CONFIG,
  // Kanaal-shapes krijgen dezelfde warm-dusk shift als de planet-grid
  // (lifeCross.personality + lifeCross.design): de personality-helft wordt
  // een warm-bruine bijna-zwart en de design-helft een terracotta rood
  // i.p.v. neon rood. De gate-circles (gateCircleFill) volgen de
  // personality-tint zodat de actieve poorten visueel bij de kanalen
  // aansluiten.
  channelPersonality: 'rgb(35,28,25)',
  channelDesign: 'rgb(192,80,70)',
  gateCircleFill: 'rgb(35,28,25)',
  // Ongedefinieerde en open centra delen dezelfde warm-wit familie als de
  // achtergrond van ongedefinieerde poorten in de planet-grid (inverse
  // variant): rgb(249, 247, 241). centerOpen = exact diezelfde warme tint,
  // centerUndefined = ~6 punten donkerder per kanaal zodat het huidige
  // grijsverschil tussen volledig open en ongedefinieerd behouden blijft —
  // maar nu in warme tint i.p.v. neutrale grijs.
  centerUndefined: 'rgb(243,241,235)',
  centerOpen: 'rgb(249,247,241)',
  centerDefined: {
    hoofd: 'rgb(236,240,140)',       // iets lichter warm geel
    ajna: 'rgb(112,222,132)',        // iets lichter warm/duskier groen
    keel: 'rgb(214,184,138)',        // iets lichter warm amber
    zelf: 'rgb(250,236,122)',        // iets lichter warm geel
    hart: 'rgb(244,138,148)',        // iets lichter, minder roze, terracotta-toets
    milt: 'rgb(250,210,158)',        // iets lichter warm amber
    sacraal: 'rgb(240,108,102)',     // iets lichter, minder neon, terracotta-toets
    solarplexus: 'rgb(244,164,90)',  // iets lichter, minder neon
    root: 'rgb(180,138,82)',         // iets lichter warm amber
  },
};

/**
 * Warm Dusk Subtle — een balans-variant tussen Warm Dusk, Enhanced Deep en
 * Enhanced Light voor de centers. Elke kleur is het rekenkundig gemiddelde
 * van de drie bronnen, wat resulteert in een palet dat de warm-dusk toets
 * behoudt maar tegelijk de helderheid van Enhanced Light en de leesbaarheid
 * van Enhanced Deep meeneemt — een rustigere, evenwichtigere chart-look.
 */
export const HD_BODYGRAPH_WARM_DUSK_SUBTLE_COLOR_CONFIG: Readonly<HdBodygraphColorConfig> = {
  ...HD_BODYGRAPH_BASE_COLOR_CONFIG,
  // Kanaal-shapes: subtiele warm-dusk shift — minder uitgesproken dan
  // Warm Dusk maar wel duidelijk weg van puur zwart/neon-rood. Sluit aan
  // bij planet-grid Warm Dusk Subtle (lifeCross). gateCircleFill volgt
  // de personality-tint.
  channelPersonality: 'rgb(29,24,22)',
  channelDesign: 'rgb(196,73,66)',
  gateCircleFill: 'rgb(29,24,22)',
  // Zelfde logica als Warm Dusk: de planet-grid undefinedBackgroundColor
  // voor de subtle variant is rgb(252, 251, 248) — die nemen we als
  // centerOpen, en centerUndefined zit ~6 punten lager per kanaal zodat
  // het grijsverschil bewaard blijft maar in warm-wit i.p.v. neutraal grijs.
  centerUndefined: 'rgb(246,245,242)',
  centerOpen: 'rgb(252,251,248)',
  centerDefined: {
    hoofd: 'rgb(235,243,127)',       // balans: warm dusk + enhanced deep + enhanced light
    ajna: 'rgb(93,237,134)',
    keel: 'rgb(215,184,129)',
    zelf: 'rgb(253,244,103)',
    hart: 'rgb(251,135,150)',
    milt: 'rgb(252,213,151)',
    sacraal: 'rgb(250,97,93)',
    solarplexus: 'rgb(251,159,80)',
    root: 'rgb(182,140,81)',
  },
};

/** Motorcenters (Pitzal-groepering): exacte waarde uit in-me.ru (#7B241C). */
const HD_BODYGRAPH_WHALES_MOTOR_COLOR = 'rgb(123,36,28)';
/** Bewustzijnscenters: exacte waarde uit in-me.ru (#043551). */
const HD_BODYGRAPH_WHALES_AWARENESS_COLOR = 'rgb(4,53,81)';
/** Expressie/realisatie: gesampeld uit Svetlana's desktopsoftware (#D2AD76). */
const HD_BODYGRAPH_WHALES_EXPRESSION_COLOR = 'rgb(210,173,118)';

/**
 * Whale, naar КИТЫ ЛИЧНОСТИ ("Whales of Personality", Svetlana Gafurova, in-me.ru): de
 * centers krijgen de kleur van hun functiegroep volgens de Pitzal-school, niet
 * een eigen kleur per center. Hoort een center bij twee groepen, dan wint de
 * "sterkere" functie:
 * - motor (donkerrood): sacraal, root (motor > druk), solar plexus
 *   (motor > bewustzijn), ego (motor > expressie, haar eigen voorbeeld);
 * - bewustzijn (navy): ajna, milt;
 * - expressie/realisatie (amber): keel en G, zoals in haar chart;
 * - druk (amber): het hoofd, alleen over na root. Deelt de kleur met G, net als
 *   in haar online bodygraph-rekenaar (in-me.ru/hd-chart: head = identity).
 * Bonuspreset, geen default. Kanalen, poortcirkels en de ongedefinieerde/open
 * centra volgen Warm Dusk; enkel de gedefinieerde centers verschillen.
 */
export const HD_BODYGRAPH_WHALES_OF_PERSONALITY_COLOR_CONFIG: Readonly<HdBodygraphColorConfig> = {
  ...HD_BODYGRAPH_WARM_DUSK_COLOR_CONFIG,
  centerDefined: {
    hoofd: HD_BODYGRAPH_WHALES_EXPRESSION_COLOR,
    ajna: HD_BODYGRAPH_WHALES_AWARENESS_COLOR,
    keel: HD_BODYGRAPH_WHALES_EXPRESSION_COLOR,
    zelf: HD_BODYGRAPH_WHALES_EXPRESSION_COLOR,
    hart: HD_BODYGRAPH_WHALES_MOTOR_COLOR,
    milt: HD_BODYGRAPH_WHALES_AWARENESS_COLOR,
    sacraal: HD_BODYGRAPH_WHALES_MOTOR_COLOR,
    solarplexus: HD_BODYGRAPH_WHALES_MOTOR_COLOR,
    root: HD_BODYGRAPH_WHALES_MOTOR_COLOR,
  },
};

export const HD_BODYGRAPH_COLOR_CONFIG_PRESETS: readonly HdBodygraphColorConfigPreset[] = [
  { id: 'enhanced-deep', label: 'Enhanced Deep', config: HD_BODYGRAPH_ENHANCED_DEEP_COLOR_CONFIG },
  { id: 'enhanced-light', label: 'Enhanced Light', config: HD_BODYGRAPH_ENHANCED_LIGHT_COLOR_CONFIG },
  { id: 'warm-dusk', label: 'Warm Dusk', config: HD_BODYGRAPH_WARM_DUSK_COLOR_CONFIG },
  { id: 'warm-dusk-subtle', label: 'Warm Dusk Subtle', config: HD_BODYGRAPH_WARM_DUSK_SUBTLE_COLOR_CONFIG },
  { id: 'classic', label: 'Classic', config: HD_BODYGRAPH_CLASSIC_COLOR_CONFIG },
  // Themanaam "Whale" in beide talen (Steven 2026-10-18).
  { id: 'whales-of-personality', label: 'Whale', config: HD_BODYGRAPH_WHALES_OF_PERSONALITY_COLOR_CONFIG },
];

// Default = 'warm-dusk' (Steven 2026-10-14); hoort bij `colorConfigPresetId: 'warm-dusk'` in de settings-defaults.
export const HD_BODYGRAPH_DEFAULT_COLOR_CONFIG = HD_BODYGRAPH_WARM_DUSK_COLOR_CONFIG;
